import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileSearch, LayoutTemplate, Sparkles, Undo2, X } from "lucide-react";
import type { DashboardInput, Widget } from "@cd/core";
import { suggestViz } from "@cd/viz";
import { api, type Catalog } from "../../lib/api";
import { selectionLabel, type TimeSelection } from "../../lib/time";
import { useDebouncedValue } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Dialog, Sheet } from "../../components/ui/overlay";
import { Provenance, TrustBadge, WarningList } from "../../components/viz/Trust";
import { useWidgetQuery, VizBody, WidgetState } from "../../components/viz/WidgetView";
import { CatalogPane } from "./Catalog";
import { ConfigPane } from "./ConfigPane";
import { draftFromDefinition, draftPack, draftToDefinition, draftToQuery, useBuilder } from "./store";
import { templatesFor } from "./templates";

interface Props {
  time: TimeSelection;
  locationIds?: string[];
  onSaved: (w: Widget, isNew: boolean) => void;
  /** Opens Ask on this widget; unset for people without Ask. */
  onAsk?: () => void;
  /** The dashboard's input boxes, so an input sharing an id with one says so. */
  dashboardInputs?: DashboardInput[];
  /** The values the dashboard is showing; the preview uses them for inputs it shares, defaults otherwise. */
  inputValues?: Record<string, number>;
}

export function Builder({ time, locationIds, onSaved, onAsk, dashboardInputs, inputValues }: Props) {
  const { open, close, draft, update, widgetId, askEdit, undoAsk } = useBuilder();
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: api.catalog, staleTime: 5 * 60_000 });
  // The pack the widget reads; the config pane and the viz pick only need its fields.
  const active = catalog.data && draftPack(draft, catalog.data.packs);
  const [showWork, setShowWork] = useState(false);

  // Auto-pick the best visualization until the user chooses one.
  // Hidden columns aren't drawn, so only visible ones decide which charts fit (one visible metric → donut works).
  const visibleMetrics = draft.metrics.filter((m) => !m.hidden).length;
  const shape = useMemo(() => {
    const dims = draft.dimensions.map((d) => ({ type: active?.dimensions.find((x) => x.ref === d.ref)?.type ?? "string", grain: d.grain }));
    return { dimensions: dims, metrics: visibleMetrics };
  }, [draft.dimensions, visibleMetrics, active]);
  useEffect(() => {
    if (draft.vizPinned || !visibleMetrics) return;
    const t = suggestViz(shape as never);
    if (t !== draft.viz.type) update((d) => ({ ...d, viz: { type: t, config: {} } }));
  }, [shape, draft.vizPinned, visibleMetrics, draft.viz.type, update]);

  const query = useDebouncedValue(useMemo(() => ({ ...draftToQuery(draft), timeRange: undefined, timePreset: undefined, ...time }), [draft, time]), 250);
  const result = useWidgetQuery(open && draft.metrics.length ? query : null, locationIds, { source: "builder", widgetId: widgetId ?? undefined }, undefined, inputValues);

  const save = useMutation({
    mutationFn: async () => {
      const def = draftToDefinition(draft);
      return widgetId ? api.updateWidget(widgetId, def) : api.createWidget(def);
    },
    onSuccess: (w) => {
      onSaved(w, !widgetId);
      close();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()} title="Widget builder" className="flex h-[min(900px,94vh)] w-[min(1400px,96vw)] flex-col overflow-hidden">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <input
          value={draft.title}
          onChange={(e) => update((d) => ({ ...d, title: e.target.value }))}
          className="min-w-0 flex-1 bg-transparent text-base font-semibold outline-none placeholder:text-subtle"
          placeholder="Widget title"
          aria-label="Widget title"
        />
        {result.data && <TrustBadge certified={result.data.certified} />}
        <span className="hidden text-xs text-subtle sm:inline">Previewing {selectionLabel(time)}</span>
        {onAsk && <Button variant="ghost" onClick={onAsk}><Sparkles className="size-3.5" /> Ask</Button>}
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button variant="primary" disabled={!draft.metrics.length || save.isPending || !!result.error} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : widgetId ? "Save changes" : "Add to dashboard"}
        </Button>
        <button onClick={close} className="rounded-md p-1 text-subtle hover:bg-surface-2" aria-label="Close">
          <X className="size-4" />
        </button>
      </header>

      {catalog.data && active ? (
        <div className="grid min-h-0 flex-1 grid-cols-[260px_minmax(0,1fr)_300px]">
          <aside className="min-h-0 border-r border-border">
            <CatalogPane packs={catalog.data.packs} />
          </aside>
          <main className="flex min-h-0 min-w-0 flex-col gap-3 bg-background p-4">
            {askEdit && (
              <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-accent/30 bg-accent-soft px-3 py-2 text-sm">
                <Sparkles className="size-4 shrink-0 text-accent" />
                <span className="mr-1 font-medium">{askEdit.chips.length ? "Ask changed:" : "Ask didn't change anything."}</span>
                {askEdit.chips.map((c) => (
                  <span key={c} className="rounded-full border border-border bg-surface px-2 py-0.5 text-xs">{c}</span>
                ))}
                <Button size="sm" variant="ghost" className="ml-auto" onClick={undoAsk}><Undo2 className="size-3.5" /> Undo</Button>
              </div>
            )}
            {draft.assumptions && draft.assumptions.length > 0 && (
              <div className="flex items-start gap-2 rounded-xl border border-accent/30 bg-accent-soft px-3 py-2 text-sm">
                <Sparkles className="mt-0.5 size-4 shrink-0 text-accent" />
                <div className="flex-1">
                  <p className="font-medium">Built from your question. Please check these interpretations:</p>
                  <ul className="mt-0.5 list-disc pl-4 text-muted">
                    {draft.assumptions.map((a) => <li key={a}>{a}</li>)}
                  </ul>
                </div>
                <button onClick={() => update((d) => ({ ...d, assumptions: undefined }))} className="text-subtle hover:text-foreground" aria-label="Dismiss">
                  <X className="size-4" />
                </button>
              </div>
            )}
            <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-[14px] border border-border bg-surface p-4 shadow-card">
              {!draft.metrics.length ? (
                // Templates name no pack, so they're offered from the first one.
                <TemplateGallery catalog={catalog.data.packs[0]!} />
              ) : (
                <>
                  <WidgetState query={result} />
                  {result.data && !result.error && <VizBody viz={draft.viz} result={result.data} />}
                </>
              )}
            </div>
            {result.data && !result.error && draft.metrics.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="flex-1"><WarningList warnings={result.data.warnings} /></div>
                <Button size="sm" variant="secondary" onClick={() => setShowWork(true)}>
                  <FileSearch className="size-3.5" /> Show your work
                </Button>
              </div>
            )}
          </main>
          <aside className="min-h-0 border-l border-border">
            <ConfigPane catalog={active} shape={shape} locationIds={locationIds} dashboardInputs={dashboardInputs} />
          </aside>
        </div>
      ) : (
        <div className="flex-1" />
      )}
      {save.error && <p className="border-t border-border px-4 py-2 text-sm text-bad">{(save.error as Error).message}</p>}
      <Sheet open={showWork} onOpenChange={setShowWork} title="How this is calculated">
        {result.data && <Provenance result={result.data} />}
      </Sheet>
    </Dialog>
  );
}

function TemplateGallery({ catalog }: { catalog: Catalog }) {
  const { update } = useBuilder();
  const available = useMemo(() => templatesFor(catalog), [catalog]);
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl py-6">
        <div className="flex items-center gap-2 text-sm font-medium"><LayoutTemplate className="size-4 text-subtle" /> Start from a template</div>
        <p className="mt-1 text-sm text-muted">Or pick metrics on the left and groupings to build your own. Everything stays editable.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-3">
          {available.map((t) => (
            <button
              key={t.title}
              onClick={() => update(() => ({ ...draftFromDefinition(t), vizPinned: true }))}
              className="rounded-xl border border-border p-3 text-left transition-colors hover:border-border-strong hover:bg-surface-2"
            >
              <div className="text-sm font-medium">{t.title}</div>
              <div className="mt-0.5 text-xs text-muted">{t.blurb}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
